import React from "react";

import { getParentElement } from "@impero-com/common/utils/dom";
import { assertNever } from "@impero-com/common/utils/obj";
import { isEqual } from "lodash-es";
import { Moment } from "moment-timezone";

import { DatePicker } from "components/datePicker";
import { MultiInputHelper } from "utils/multiInputHelper";

import { BaseFormDisplay } from "./formDisplay";

const helper: MultiInputHelper<Moment, never> = new MultiInputHelper({
    isEqual: (a: Moment, b: Moment) => isEqual(a, b),
    isBlank: (_: Moment) => false,
    display: (_: Moment) => {
        throw new Error("MultiDatePicker has no display() function defined.");
    },
});

type MultiDatePickerProps =
    | ({ type: "EDIT" } & WrappedWriteProps)
    | ({ type: "READ_ONLY" } & WrappedReadProps);

type Wrapped<T> = T & { label: string };

interface ReadProps {
    name: string;
    values?: Moment[];
    className?: string;
}

type WrappedReadProps = Wrapped<ReadProps>;

interface WriteProps extends ReadProps {
    onChange: (v: Moment | null | undefined) => void;
    allowClear?: boolean;
}

type WrappedWriteProps = Wrapped<WriteProps>;

class MultiDatePicker extends React.PureComponent<MultiDatePickerProps> {
    render() {
        const { props } = this;
        switch (props.type) {
            case "READ_ONLY": {
                const { label, name, values, className } = props;
                return (
                    <WrappedReadOnlyDatePicker
                        label={label}
                        name={name}
                        values={values}
                        className={className}
                    />
                );
            }
            case "EDIT": {
                const { label, name, values, onChange, allowClear, className } =
                    props;
                return (
                    <WrappedEditDatePicker
                        label={label}
                        name={name}
                        values={values}
                        onChange={onChange}
                        allowClear={allowClear}
                        className={className}
                    />
                );
            }
            default:
                return assertNever(props);
        }
    }
}

type RawReadOnlyDatePickerProps = ReadProps;

class RawReadOnlyDatePicker extends React.PureComponent<RawReadOnlyDatePickerProps> {
    render() {
        const { value, placeholder } = helper.deriveValueAndPlaceholder(
            this.props.values
        );
        return (
            <DatePicker
                className={this.props.className}
                value={value}
                disabled={true}
                placeholder={placeholder}
            />
        );
    }
}

interface RawEditableDatePickerProps extends WriteProps {
    allowClear?: boolean;
}

interface RawEditableDatePickerState {
    derivedValue: Moment | null | undefined;
    trackedValue: Moment | null | undefined;
    placeholder: string;
}

class RawEditableDatePicker extends React.Component<
    RawEditableDatePickerProps,
    RawEditableDatePickerState
> {
    state: RawEditableDatePickerState = {
        derivedValue: undefined,
        trackedValue: undefined,
        placeholder: "",
    };

    static getDerivedStateFromProps(
        props: RawEditableDatePickerProps,
        state: RawEditableDatePickerState
    ): RawEditableDatePickerState | null {
        const { value, placeholder } = helper.deriveValueAndPlaceholder(
            props.values
        );
        if (
            !isEqual(value, state.derivedValue) ||
            placeholder !== state.placeholder
        ) {
            return {
                derivedValue: value,
                trackedValue: value,
                placeholder,
            };
        }
        return null;
    }

    render() {
        const { state, props } = this;
        const { trackedValue, placeholder } = state;
        const { className, allowClear } = props;

        return (
            <DatePicker
                className={className}
                value={trackedValue}
                onChange={this.onChange}
                placeholder={placeholder}
                allowClear={allowClear}
                getPopupContainer={getParentElement}
            />
        );
    }
    onChange = (value: Moment | null | undefined): void => {
        if (value) {
            value = value.hours(0).minutes(0).seconds(0).milliseconds(0);
        }
        this.setState({
            trackedValue: value,
        });
        this.props.onChange(value);
    };
}

class WrappedReadOnlyDatePicker extends React.PureComponent<WrappedReadProps> {
    render() {
        const { label, name, values, className } = this.props;
        return (
            <BaseFormDisplay label={label}>
                <RawReadOnlyDatePicker
                    name={name}
                    values={values}
                    className={className}
                />
            </BaseFormDisplay>
        );
    }
}

class WrappedEditDatePicker extends React.PureComponent<WrappedWriteProps> {
    render() {
        const { label, name, values, className, onChange, allowClear } =
            this.props;
        return (
            <BaseFormDisplay label={label}>
                <RawEditableDatePicker
                    name={name}
                    values={values}
                    className={className}
                    onChange={onChange}
                    allowClear={allowClear}
                />
            </BaseFormDisplay>
        );
    }
}

export {
    MultiDatePicker,
    type MultiDatePickerProps,
    RawEditableDatePicker,
    RawReadOnlyDatePicker,
};
